import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import * as Yup from "yup";
import Logo from "../assets/Logo.png";
import { industries } from "../utils";
import { useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import { CircleRounded } from "@mui/icons-material";
import { yupResolver } from "@hookform/resolvers/yup";
import { DarkSelect, DarkTextField } from "./styles";

const schema = Yup.object({
  email: Yup.string()
    .email("Must be a valid email")
    .required("Email is required")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Only corporate email addresses are allowed"
    )
    .test(
      "is-corporate-email",
      "Use of personal email addresses is not allowed",
      (value) =>
        !/@gmail\.com\s*$|@yahoo\.com\s*$|@hotmail\.com\s*$/.test(value)
    ),
  industry: Yup.string()
    .matches(
      /^[A-Za-z]+(?: [A-Za-z]+)*$/,
      "Industry must only contain letters."
    )
    .min(2, "Industry must be at least 2 characters long")
    .max(50, "Industry can't be longer than 50 characters")
    .required("Industry is required"),
}).required();

const InputPage = ({ onSubmitForm }) => {
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState(1);
  const loading = useSelector((state) => state.user.loadingButton);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  const handleNext = async () => {
    if (currentStep < 2 && isValid) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === 1) {
      const isEmailValid = await trigger("email");
      if (isEmailValid) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  useEffect(() => {
    register("industry");
  }, [register]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          marginY: "4%",
          bgcolor: "#01081D",
          opacity: "90%",
          borderRadius: "24px",
          padding: "24px",
        }}
      >
        <Box display={"flex"} direction="row" justifyContent="center" mb={2}>
          <img src={Logo} alt="stat-icon" width={240} />
        </Box>

        <Box display="flex" justifyContent="center" alignItems="center" p={5}>
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} md={6}>
              <Typography variant="h1" color={"#fff"}>
                Predict Your Industry's
              </Typography>
              <Typography variant="h1" color={"green.main"}>
                Growth!
              </Typography>
              <Box my={2} sx={{ width: "70%" }}>
                <Typography variant="subtitle1" color={"#fff"}>
                  See tomorrow today, Revolutionize your business strategy with
                  predictive AI.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6} my={4} sx={{ alignContent: "center" }}>
              <Box
                component="form"
                onSubmit={handleSubmit(onSubmitForm)}
                sx={{ minWidth: "400px" }}
              >
                {currentStep === 1 ? (
                  <>
                    <DarkTextField
                      {...register("email")}
                      label="Email"
                      variant="filled"
                      type="email"
                      name="email"
                      id="emaill"
                      inputProps={{
                        maxLength: 60,
                      }}
                      fullWidth
                      required
                      error={!!errors.email?.message}
                      helperText={
                        errors.email?.message ? errors.email.message : "\u00A0"
                      }
                    />
                  </>
                ) : currentStep === 2 ? (
                  <>
                    <Controller
                      name="industry"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <DarkSelect
                          {...field}
                          options={industries}
                          freeSolo
                          disablePortal
                          variant="filled"
                          onInputChange={(event, newInputValue, reason) => {
                            if (reason === "input") {
                              setValue("industry", newInputValue, {
                                shouldValidate: true,
                              });
                            }
                          }}
                          onChange={(event, newValue, reason) => {
                            if (reason === "selectOption") {
                              setValue("industry", newValue, {
                                shouldValidate: true,
                              });
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select or Type Industry"
                              variant="filled"
                              InputProps={{
                                ...params.InputProps,
                                style: { backgroundColor: "white" },
                              }}
                              error={!!error}
                              helperText={error ? error.message : "\u00A0"}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li
                              {...props}
                              style={{ borderBottom: "1px solid #C4CDD5" }}
                            >
                              <Box
                                component={CircleRounded}
                                sx={{
                                  width: 17,
                                  height: 40,
                                  mr: 2,
                                  color: "green.main",
                                }}
                              />

                              <Box
                                sx={{
                                  flexGrow: 1,
                                  "& span": {
                                    color:
                                      theme.palette.mode === "light"
                                        ? "#586069"
                                        : "#8b949e",
                                  },
                                }}
                              >
                                {option}
                                <br />
                              </Box>
                            </li>
                          )}
                        />
                      )}
                    />
                  </>
                ) : (
                  ""
                )}

                <Box my={2}>
                  {currentStep === 1 ? (
                    <Button
                      onClick={handleNext}
                      type={"button"}
                      variant="contained"
                      fullWidth
                      sx={{ borderRadius: "34px", fontSize: 20 }}
                      color={"green"}
                    >
                      Continue
                    </Button>
                  ) : currentStep === 2 ? (
                    <LoadingButton
                      type="submit"
                      variant="contained"
                      fullWidth
                      sx={{ borderRadius: "34px", fontSize: 20 }}
                      color={"green"}
                      loading={loading}
                      loadingIndicator={
                        <CircularProgress color="inherit" size={16} />
                      }
                    >
                      Predict now
                    </LoadingButton>
                  ) : (
                    ""
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default InputPage;
